import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { requireAuth } from "../lib/authGate";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { BookOpen, Search, Library as LibraryIcon, User, LogOut, Moon, Sun, Scroll, Sparkles, BookMarked, Menu, Home, X, Heart, Share2, Shield, NotebookPen, Calendar as CalendarIcon, ScrollText } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import ShareAppModal from "./ShareAppModal";
import AnimatedBookLogo from "./AnimatedBookLogo";
import BrandMark from "./BrandMark";
import LanguageToggle from "./LanguageToggle";

const linkClass = ({ isActive }) =>
  `relative px-3 py-2 font-heading text-sm tracking-wide transition-colors ${
    isActive ? "text-gold" : "text-foreground/80 hover:text-foreground"
  }`;

const ThemeIcon = ({ theme }) => {
  if (theme === "dark") return <Moon className="h-4 w-4" strokeWidth={1.5} />;
  if (theme === "white") return <Sun className="h-4 w-4" strokeWidth={1.5} />;
  return <Scroll className="h-4 w-4" strokeWidth={1.5} />;
};

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, update } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const closeMobile = () => setMobileOpen(false);
  const { t } = useTranslation();

  const mobileLinks = [
    { to: "/", label: t("nav.home"), icon: Home, end: true, testid: "mobile-nav-home" },
    { to: "/library", label: t("nav.library"), icon: LibraryIcon, testid: "mobile-nav-library" },
    { to: "/daily", label: t("nav.daily"), icon: Sparkles, testid: "mobile-nav-daily" },
    { to: "/siddur", label: t("nav.siddur"), icon: ScrollText, testid: "mobile-nav-siddur" },
    { to: "/calendar", label: t("nav.calendar"), icon: CalendarIcon, testid: "mobile-nav-calendar" },
    { to: "/beit-midrash", label: t("nav.beit_midrash"), icon: BookMarked, testid: "mobile-nav-beit-midrash" },
    { to: "/notebook", label: t("nav.notebook"), icon: NotebookPen, testid: "mobile-nav-notebook" },
    { to: "/search", label: t("nav.search"), icon: Search, testid: "mobile-nav-search" },
    { to: "/donate", label: t("nav.donate"), icon: Heart, testid: "mobile-nav-donate" },
  ];

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-40 border-b border-border/60 bg-background/85 backdrop-blur-md"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link
          to="/"
          data-testid="nav-home"
          className="flex items-center gap-2"
          aria-label={t("user_menu.navbar_logo_aria")}
        >
          <AnimatedBookLogo size={44} />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="nav-links">
          <NavLink to="/" className={linkClass} end data-testid="nav-link-home">
            {t("nav.home")}
          </NavLink>
          <NavLink to="/library" className={linkClass} data-testid="nav-link-library">
            <span className="flex items-center gap-1.5">
              <LibraryIcon className="h-4 w-4" strokeWidth={1.5} />
              {t("nav.library")}
            </span>
          </NavLink>
          <NavLink to="/daily" className={linkClass} data-testid="nav-link-daily">
            <span className="flex items-center gap-1.5">
              <Sparkles className="h-4 w-4" strokeWidth={1.5} />
              {t("nav.daily")}
            </span>
          </NavLink>
          <NavLink to="/siddur" className={linkClass} data-testid="nav-link-siddur">
            <span className="flex items-center gap-1.5">
              <ScrollText className="h-4 w-4" strokeWidth={1.5} />
              {t("nav.siddur")}
            </span>
          </NavLink>
          <NavLink to="/calendar" className={linkClass} data-testid="nav-link-calendar">
            <span className="flex items-center gap-1.5">
              <CalendarIcon className="h-4 w-4" strokeWidth={1.5} />
              {t("nav.calendar")}
            </span>
          </NavLink>
          <NavLink
            to="/beit-midrash"
            className={linkClass}
            data-testid="nav-link-beit-midrash"
            onClick={(e) => {
              if (!requireAuth("beit_midrash")) e.preventDefault();
            }}
          >
            <span className="flex items-center gap-1.5">
              <BookMarked className="h-4 w-4" strokeWidth={1.5} />
              {t("nav.beit_midrash")}
            </span>
          </NavLink>
          <NavLink
            to="/notebook"
            className={linkClass}
            data-testid="nav-link-notebook"
            onClick={(e) => {
              if (!requireAuth("notebook")) e.preventDefault();
            }}
          >
            <span className="flex items-center gap-1.5">
              <NotebookPen className="h-4 w-4" strokeWidth={1.5} />
              {t("nav.notebook")}
            </span>
          </NavLink>
          <NavLink to="/search" className={linkClass} data-testid="nav-link-search">
            <span className="flex items-center gap-1.5">
              <Search className="h-4 w-4" strokeWidth={1.5} />
              {t("nav.search")}
            </span>
          </NavLink>
          <NavLink to="/donate" className={linkClass} data-testid="nav-link-donate">
            <span className="flex items-center gap-1.5">
              <Heart className="h-4 w-4" strokeWidth={1.5} />
              {t("nav.donate")}
            </span>
          </NavLink>
        </nav>

        <div className="flex items-center gap-2">
          {/* Mobile hamburger — shown up to and INCLUDING tablet portrait
              (iPad Pro 11" = 834px) and phone landscape (≤844px). The
              old `md:hidden` breakpoint kicked in at 768px which crammed
              9 nav links + toggles into iPad-portrait widths. Raising
              the threshold to `lg:hidden` (1024px) gives every tablet
              size a clean hamburger drawer instead. */}
          <Button
            variant="ghost"
            size="icon"
            data-testid="mobile-menu-trigger"
            className="lg:hidden text-foreground hover:text-gold hover:bg-gold/10"
            aria-label={t("user_menu.nav_menu_aria")}
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-5 w-5" strokeWidth={1.5} />
          </Button>
          {/* Language toggle (HE / EN) — compact on phones, full text on tablet+ */}
          <LanguageToggle compact className="sm:hidden" />
          <LanguageToggle className="hidden sm:inline-flex" />
          {/* Theme selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="theme-toggle"
                className="text-foreground hover:text-gold hover:bg-gold/10"
                aria-label={t("theme.label")}
              >
                <ThemeIcon theme={theme} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
              <DropdownMenuLabel className="font-heading">{t("theme.label")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="theme-parchment"
                onClick={() => update({ theme: "parchment" })}
                className={theme === "parchment" ? "text-gold" : ""}
              >
                <Scroll className="ms-2 h-4 w-4" strokeWidth={1.5} /> {t("theme.parchment")}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="theme-dark"
                onClick={() => update({ theme: "dark" })}
                className={theme === "dark" ? "text-gold" : ""}
              >
                <Moon className="ms-2 h-4 w-4" strokeWidth={1.5} /> {t("theme.dark")}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="theme-white"
                onClick={() => update({ theme: "white" })}
                className={theme === "white" ? "text-gold" : ""}
              >
                <Sun className="ms-2 h-4 w-4" strokeWidth={1.5} /> {t("theme.white")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid="user-menu-trigger"
                  className="text-foreground hover:text-gold hover:bg-gold/10"
                  aria-label={t("user_menu.trigger_aria")}
                >
                  <User className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[200px]">
                <DropdownMenuLabel className="font-heading">
                  {user.name || user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/beit-midrash")} data-testid="menu-profile">
                  <BookMarked className="ms-2 h-4 w-4" strokeWidth={1.5} />
                  {t("user_menu.beit_midrash")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShareOpen(true)} data-testid="menu-share">
                  <Share2 className="ms-2 h-4 w-4" strokeWidth={1.5} />
                  {t("user_menu.share_app")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/donate")} data-testid="menu-donate">
                  <Heart className="ms-2 h-4 w-4" strokeWidth={1.5} />
                  {t("user_menu.donate")}
                </DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <Shield className="ms-2 h-4 w-4" strokeWidth={1.5} />
                    {t("user_menu.admin_dashboard")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                  <LogOut className="ms-2 h-4 w-4" strokeWidth={1.5} /> {t("user_menu.sign_out")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              data-testid="nav-login-btn"
              onClick={() => navigate("/auth")}
              className="sacred-btn hidden sm:inline-flex"
            >
              {t("common.login")}
            </Button>
          )}
        </div>
      </div>

      {/* Mobile navigation drawer (right-edge in RTL = visually slides from the side
          users expect on a Hebrew interface). */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="right"
          dir="rtl"
          data-testid="mobile-nav-drawer"
          className="w-[80%] max-w-xs bg-background border-s border-gold/30 p-0 flex flex-col"
        >
          <SheetHeader className="text-right p-5 border-b border-border/50">
            <SheetTitle className="flex items-center gap-3 text-right">
              <BrandMark size={40} className="rounded-md shadow-sm shrink-0" />
              <span className="flex flex-col leading-tight">
                <span className="font-heading text-2xl text-foreground">תוריא</span>
                <span className="font-latin italic text-[11px] text-muted-foreground tracking-widest uppercase">
                  Torahya
                </span>
              </span>
            </SheetTitle>
          </SheetHeader>

          <nav
            className="flex-1 overflow-y-auto py-3"
            aria-label={t("user_menu.main_nav_aria")}
            data-testid="mobile-nav-list"
          >
            {mobileLinks.map(({ to, label, icon: Icon, end, testid }) => {
              // Gate /beit-midrash and /notebook for anonymous users in
              // the mobile nav drawer too.
              const gatedFeature =
                to === "/beit-midrash"
                  ? "beit_midrash"
                  : to === "/notebook"
                  ? "notebook"
                  : null;
              return (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={(e) => {
                    if (gatedFeature && !requireAuth(gatedFeature)) {
                      e.preventDefault();
                      closeMobile();
                      return;
                    }
                    closeMobile();
                  }}
                  data-testid={testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-6 py-4 font-heading text-lg border-r-2 transition-colors ${
                      isActive
                        ? "text-gold border-gold bg-gold/5"
                        : "text-foreground/85 border-transparent hover:bg-gold/5 hover:text-foreground"
                  }`
                }
              >
                <Icon className="h-5 w-5" strokeWidth={1.5} />
                <span>{label}</span>
              </NavLink>
              );
            })}
          </nav>

          <div className="border-t border-border/50 p-4 space-y-2">
            {user ? (
              <>
                <p className="px-2 text-xs text-muted-foreground truncate" data-testid="mobile-nav-user-email">
                  {user.name || user.email}
                </p>
                <Button
                  variant="ghost"
                  onClick={() => {
                    closeMobile();
                    handleLogout();
                  }}
                  data-testid="mobile-nav-logout"
                  className="w-full justify-start sacred-btn-ghost"
                >
                  <LogOut className="h-4 w-4 ms-2" strokeWidth={1.5} />
                  {t("user_menu.sign_out")}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => {
                  closeMobile();
                  navigate("/auth");
                }}
                data-testid="mobile-nav-login"
                className="w-full sacred-btn"
              >
                {t("common.login")}
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
      <ShareAppModal open={shareOpen} onClose={() => setShareOpen(false)} />
    </header>
  );
}
